import React, { useState, useEffect, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

export default function NumberPools() {
  const { user } = useAuth({ requiredRole: "manager" });

  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolDetail, setPoolDetail] = useState(null);
  const [orgNumbers, setOrgNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Load pools
  const loadPools = useCallback(async () => {
    try {
      const res = await api.get("/number-pools");
      setPools(res.data.pools || []);
    } catch (err) {
      setError("Failed to load pools");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load pool detail
  const loadPoolDetail = useCallback(async (poolId) => {
    try {
      const res = await api.get(`/number-pools/${poolId}`);
      setPoolDetail(res.data.pool);
    } catch (err) {
      setError("Failed to load pool details");
    }
  }, []);

  // Load unassigned numbers
  const loadOrgNumbers = useCallback(async () => {
    try {
      const res = await api.get("/phone-numbers?unassigned=true");
      setOrgNumbers(res.data.phone_numbers || []);
    } catch (err) {
      console.error("Failed to load numbers:", err);
    }
  }, []);

  useEffect(() => {
    loadPools();
    loadOrgNumbers();
  }, [loadPools, loadOrgNumbers]);

  useEffect(() => {
    if (selectedPool) {
      loadPoolDetail(selectedPool);
    } else {
      setPoolDetail(null);
    }
  }, [selectedPool, loadPoolDetail]);

  if (!user) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Number Pools</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowProvisionModal(true)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            + Provision Number
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            + Create Pool
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pool list */}
        <div className="space-y-3">
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wide">All Pools</h2>
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-xl" />
              ))}
            </div>
          ) : pools.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-6 text-center">
              <p className="text-gray-400 text-sm">No pools yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-2 text-blue-600 text-sm font-medium hover:underline"
              >
                Create your first pool
              </button>
            </div>
          ) : (
            pools.map((pool) => (
              <button
                key={pool.id}
                onClick={() => setSelectedPool(pool.id)}
                className={`w-full text-left bg-white rounded-xl border p-4 transition-all hover:shadow-sm ${
                  selectedPool === pool.id
                    ? "border-blue-500 ring-2 ring-blue-100"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-gray-900 text-sm">{pool.name}</h3>
                  {pool.sticky_sender && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      Sticky
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-3 text-xs text-gray-500">
                  <span>{pool.number_count} number{pool.number_count !== 1 ? "s" : ""}</span>
                  <span>{pool.mms_capable_count} MMS</span>
                  {pool.messaging_service_sid && (
                    <span className="text-purple-500 font-mono truncate max-w-[120px]">
                      {pool.messaging_service_sid}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Pool detail */}
        <div className="lg:col-span-2">
          {poolDetail ? (
            <PoolDetailView
              pool={poolDetail}
              orgNumbers={orgNumbers}
              onUpdate={() => { loadPoolDetail(selectedPool); loadPools(); loadOrgNumbers(); }}
              onDelete={() => { setSelectedPool(null); setPoolDetail(null); loadPools(); }}
              onAssignCampaign={() => setShowAssignModal(true)}
            />
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <p className="text-gray-400 text-sm">Select a pool to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Pool Modal */}
      {showCreateModal && (
        <CreatePoolModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadPools(); }}
        />
      )}

      {/* Provision Number Modal */}
      {showProvisionModal && (
        <ProvisionModal
          pools={pools}
          onClose={() => setShowProvisionModal(false)}
          onProvisioned={() => { setShowProvisionModal(false); loadOrgNumbers(); if (selectedPool) loadPoolDetail(selectedPool); loadPools(); }}
        />
      )}

      {/* Assign Campaign Modal */}
      {showAssignModal && selectedPool && (
        <AssignCampaignModal
          poolId={selectedPool}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => { setShowAssignModal(false); loadPoolDetail(selectedPool); }}
        />
      )}
    </div>
  );
}


// ── Pool Detail View ──
function PoolDetailView({ pool, orgNumbers, onUpdate, onDelete, onAssignCampaign }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: pool.name, sticky_sender: pool.sticky_sender, max_mps: pool.max_mps || "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ name: pool.name, sticky_sender: pool.sticky_sender, max_mps: pool.max_mps || "" });
    setEditing(false);
  }, [pool.id]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/number-pools/${pool.id}`, {
        name: form.name,
        sticky_sender: form.sticky_sender,
        max_mps: form.max_mps ? parseInt(form.max_mps) : null,
      });
      setEditing(false);
      onUpdate();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to update pool"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete pool "${pool.name}"? Numbers will be unassigned.`)) return;
    try {
      await api.delete(`/number-pools/${pool.id}`);
      onDelete();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to delete pool"));
    }
  };

  const handleAddNumber = async (phoneId) => {
    try {
      await api.post(`/number-pools/${pool.id}/numbers`, { phone_number_id: phoneId });
      onUpdate();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to add number"));
    }
  };

  const handleRemoveNumber = async (phoneId) => {
    try {
      await api.delete(`/number-pools/${pool.id}/numbers/${phoneId}`);
      onUpdate();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to remove number"));
    }
  };

  return (
    <div className="space-y-4">
      {/* Pool header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          {editing ? (
            <input
              type="text"
              className="text-lg font-bold text-gray-900 border border-gray-300 rounded-lg px-2 py-1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          ) : (
            <h2 className="text-lg font-bold text-gray-900">{pool.name}</h2>
          )}
          <div className="flex space-x-2">
            {editing ? (
              <>
                <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                  {saving ? "Saving..." : "Save"}
                </button>
                <button onClick={() => setEditing(false)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Edit
                </button>
                <button onClick={onAssignCampaign} className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200">
                  Assign Campaign
                </button>
                <button onClick={handleDelete} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200">
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Pool config */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-400">Numbers</p>
            <p className="text-xl font-bold text-gray-900">{pool.number_count}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">MMS Capable</p>
            <p className="text-xl font-bold text-green-600">{pool.mms_capable_count}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Sticky Sender</p>
            {editing ? (
              <label className="flex items-center space-x-2 mt-1">
                <input
                  type="checkbox"
                  checked={form.sticky_sender}
                  onChange={(e) => setForm({ ...form, sticky_sender: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm">{form.sticky_sender ? "On" : "Off"}</span>
              </label>
            ) : (
              <p className={`text-xl font-bold ${pool.sticky_sender ? "text-green-600" : "text-gray-400"}`}>
                {pool.sticky_sender ? "On" : "Off"}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-400">Max MPS</p>
            {editing ? (
              <input
                type="number"
                className="w-20 border border-gray-300 rounded px-2 py-1 text-sm mt-1"
                value={form.max_mps}
                onChange={(e) => setForm({ ...form, max_mps: e.target.value })}
                placeholder="Auto"
              />
            ) : (
              <p className="text-xl font-bold text-gray-900">{pool.max_mps || "Auto"}</p>
            )}
          </div>
        </div>

        {pool.messaging_service_sid && (
          <div className="mt-3 bg-purple-50 rounded-lg px-3 py-2">
            <span className="text-xs text-purple-600 font-medium">Messaging Service: </span>
            <span className="text-xs text-purple-700 font-mono">{pool.messaging_service_sid}</span>
          </div>
        )}
      </div>

      {/* Numbers in pool */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">Numbers in Pool</h3>
        </div>

        {pool.numbers && pool.numbers.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {pool.numbers.map((num) => (
              <div key={num.id} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <span className="font-mono text-sm text-gray-900">{num.number}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    num.number_type === "toll_free"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {num.number_type || "local"}
                  </span>
                  {num.mms_capable && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">MMS</span>
                  )}
                  {!num.is_active && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Inactive</span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveNumber(num.id)}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No numbers in this pool</p>
        )}

        {/* Add unassigned numbers */}
        {orgNumbers.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <h4 className="text-xs text-gray-400 mb-2">Add Unassigned Numbers</h4>
            <div className="flex flex-wrap gap-2">
              {orgNumbers.map((num) => (
                <button
                  key={num.id}
                  onClick={() => handleAddNumber(num.id)}
                  className="flex items-center space-x-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <span className="font-mono">{num.number}</span>
                  <span className="text-blue-500">+</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Campaigns using this pool */}
      {pool.campaigns && pool.campaigns.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
            Campaigns Using This Pool
          </h3>
          <div className="divide-y divide-gray-100">
            {pool.campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-900">{c.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  c.status === "active" ? "bg-green-100 text-green-700" :
                  c.status === "draft" ? "bg-gray-100 text-gray-600" :
                  c.status === "paused" ? "bg-yellow-100 text-yellow-700" :
                  "bg-gray-100 text-gray-600"
                }`}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Create Pool Modal ──
function CreatePoolModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    sticky_sender: true,
    max_mps: "",
    messaging_service_sid: "",
    create_messaging_service: false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      await api.post("/number-pools", {
        name: form.name.trim(),
        sticky_sender: form.sticky_sender,
        max_mps: form.max_mps ? parseInt(form.max_mps) : null,
        messaging_service_sid: form.messaging_service_sid || undefined,
        create_messaging_service: form.create_messaging_service,
      });
      onCreated();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to create pool"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Create Number Pool</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pool Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Primary 10DLC Pool"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Messaging Service SID <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500"
              value={form.messaging_service_sid}
              onChange={(e) => setForm({ ...form, messaging_service_sid: e.target.value })}
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              disabled={form.create_messaging_service}
            />
            <label className="flex items-center space-x-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.create_messaging_service}
                onChange={(e) => setForm({ ...form, create_messaging_service: e.target.checked, messaging_service_sid: "" })}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-xs text-gray-500">Auto-create Messaging Service via Twilio</span>
            </label>
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.sticky_sender}
                onChange={(e) => setForm({ ...form, sticky_sender: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">Sticky sender routing</span>
            </label>
            <div>
              <label className="text-sm text-gray-700">Max MPS</label>
              <input
                type="number"
                className="ml-2 w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                value={form.max_mps}
                onChange={(e) => setForm({ ...form, max_mps: e.target.value })}
                placeholder="Auto"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={saving || !form.name.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Creating..." : "Create Pool"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Provision Number Modal ──
function ProvisionModal({ pools, onClose, onProvisioned }) {
  const [searchParams, setSearchParams] = useState({ area_code: "", type: "local" });
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [provisioning, setProvisioning] = useState(null);
  const [selectedPool, setSelectedPool] = useState("");

  const handleSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.area_code) params.append("area_code", searchParams.area_code);
      params.append("type", searchParams.type);
      params.append("limit", "20");

      const res = await api.get(`/phone-numbers/search?${params.toString()}`);
      setResults(res.data.numbers || []);
    } catch (err) {
      alert(errorToUserMessage(err, "Search failed."));
    } finally {
      setSearching(false);
    }
  };

  const handleProvision = async (phoneNumber) => {
    setProvisioning(phoneNumber);
    try {
      await api.post("/phone-numbers/provision", {
        phone_number: phoneNumber,
        type: searchParams.type,
        number_pool_id: selectedPool ? parseInt(selectedPool) : undefined,
        mms_capable: true,
      });
      onProvisioned();
    } catch (err) {
      alert(errorToUserMessage(err, "Provisioning failed."));
    } finally {
      setProvisioning(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Provision Phone Number</h2>

        {/* Search form */}
        <div className="flex items-end space-x-3 mb-4">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Area Code</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={searchParams.area_code}
              onChange={(e) => setSearchParams({ ...searchParams, area_code: e.target.value })}
              placeholder="e.g., 202"
              maxLength={3}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={searchParams.type}
              onChange={(e) => setSearchParams({ ...searchParams, type: e.target.value })}
            >
              <option value="local">Local (10DLC)</option>
              <option value="toll_free">Toll-Free</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Assign to Pool</label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedPool}
              onChange={(e) => setSelectedPool(e.target.value)}
            >
              <option value="">No pool</option>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSearch}
            disabled={searching}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {searching ? "..." : "Search"}
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {results.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">
              {searching ? "Searching..." : "Search for available numbers above"}
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {results.map((num) => (
                <div key={num.phone_number} className="flex items-center justify-between py-2">
                  <div>
                    <span className="font-mono text-sm text-gray-900">{num.phone_number}</span>
                    <span className="text-xs text-gray-400 ml-2">
                      {num.locality}, {num.region}
                    </span>
                    <div className="flex space-x-1 mt-0.5">
                      {num.capabilities?.sms && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">SMS</span>}
                      {num.capabilities?.mms && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">MMS</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleProvision(num.phone_number)}
                    disabled={provisioning === num.phone_number}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    {provisioning === num.phone_number ? "Provisioning..." : "Provision"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Assign Campaign Modal ──
function AssignCampaignModal({ poolId, onClose, onAssigned }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/campaigns")
      .then((res) => setCampaigns(res.data.campaigns || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAssign = async (campaignId) => {
    try {
      await api.post(`/number-pools/${poolId}/assign-campaign`, { campaign_id: campaignId });
      onAssigned();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to assign"));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Assign Pool to Campaign</h2>
        {loading ? (
          <div className="animate-pulse h-20 bg-gray-100 rounded-lg" />
        ) : campaigns.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">No campaigns available</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {campaigns.map((c) => (
              <button
                key={c.id}
                onClick={() => handleAssign(c.id)}
                className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{c.name}</p>
                  <p className="text-xs text-gray-400">
                    {c.number_pool_id ? `Current pool: #${c.number_pool_id}` : "No pool assigned"}
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  c.status === "active" ? "bg-green-100 text-green-700" :
                  c.status === "draft" ? "bg-gray-100 text-gray-600" :
                  "bg-yellow-100 text-yellow-700"
                }`}>
                  {c.status}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
